import React, { useState } from "react";
import {
  FaSearch,
  FaDatabase,
  FaFileAlt,
  FaChartBar,
  FaLayerGroup,
  FaCheck,
  FaCode,
  FaGlobe,
  FaChevronRight,
  FaChevronDown,
  FaQuestionCircle,
} from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { ToolCallEntry, ChatTheme } from "./types";
import useTranslation from "@/hooks/use-translation";

// ============================================================================
// Props
// ============================================================================

interface ToolCallsTimelineProps {
  toolCalls: ToolCallEntry[];
  isLive?: boolean;
  theme: ChatTheme;
}

// ============================================================================
// Helpers
// ============================================================================

const TOOL_LABEL_FALLBACK: Record<string, string> = {
  search_elasticsearch: "Recherche",
  get_es_mapping: "Lecture schéma",
  display_documents: "Affichage documents",
  generate_widget: "Création widget",
  generate_tab: "Création onglet",
  search_endpoints: "Recherche endpoints",
  call_api: "Appel API",
  web_search: "Recherche web",
  ask_user: "Question à l'utilisateur",
  setup_monitoring: "Configuration monitoring",
  create_widget: "Création widget",
  create_dashboard_with_widgets: "Création dashboard",
};

const getToolCallLabel = (
  name: string,
  args?: Record<string, unknown>,
): string => {
  if (args?.label && typeof args.label === "string") {
    return args.label;
  }
  return TOOL_LABEL_FALLBACK[name] ?? name.replace(/_/g, " ");
};

const getToolCallIcon = (
  name: string,
  size: "sm" | "md" = "sm",
): React.ReactNode => {
  const sizeClass = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  switch (name) {
    case "search_elasticsearch":
      return <FaSearch className={sizeClass} />;
    case "get_es_mapping":
      return <FaDatabase className={sizeClass} />;
    case "display_documents":
      return <FaFileAlt className={sizeClass} />;
    case "generate_widget":
      return <FaChartBar className={sizeClass} />;
    case "generate_tab":
      return <FaLayerGroup className={sizeClass} />;
    case "search_endpoints":
      return <FaSearch className={sizeClass} />;
    case "call_api":
      return <FaGlobe className={sizeClass} />;
    case "ask_user":
      return <FaQuestionCircle className={sizeClass} />;
    default:
      return <FaCode className={sizeClass} />;
  }
};

const formatToolArgs = (args?: Record<string, unknown>): string | null => {
  if (!args || Object.keys(args).length === 0) return null;

  let parsedArgs = args;
  if (typeof args === "string") {
    try {
      parsedArgs = JSON.parse(args);
    } catch {
      return args as string;
    }
  }

  const parts: string[] = [];
  for (const [key, value] of Object.entries(parsedArgs)) {
    if (key === "label") continue;
    if (value === null || value === undefined) continue;

    if (Array.isArray(value) && value.length > 3) {
      parts.push(`${key}: [${value.length} items]`);
    } else if (typeof value === "object") {
      parts.push(`${key}: {...}`);
    } else if (typeof value === "string" && value.length > 50) {
      parts.push(`${key}: "${value.substring(0, 47)}..."`);
    } else if (typeof value === "string") {
      parts.push(`${key}: "${value}"`);
    } else {
      parts.push(`${key}: ${value}`);
    }
  }

  return parts.length > 0 ? parts.join(", ") : null;
};

const formatJsonForDisplay = (
  data: unknown,
  maxLength: number = 500,
): string => {
  if (data === null || data === undefined) return "null";
  try {
    const formatted = JSON.stringify(data, null, 2);
    if (formatted.length > maxLength) {
      return formatted.substring(0, maxLength) + "\n... (tronqué)";
    }
    return formatted;
  } catch {
    return String(data);
  }
};

const getResultSummary = (
  result: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
): string => {
  if (result === null || result === undefined) return "Aucun résultat";
  if (Array.isArray(result)) {
    return t("n_elements", { count: result.length });
  }
  if (typeof result === "object") {
    const keys = Object.keys(result as Record<string, unknown>);
    return t("n_properties", { count: keys.length });
  }
  if (typeof result === "string") {
    return result.length > 50 ? result.substring(0, 47) + "..." : result;
  }
  return String(result);
};

// ============================================================================
// Component
// ============================================================================

/**
 * Displays a timeline of tool calls with expandable details.
 */
export const ToolCallsTimeline: React.FC<ToolCallsTimelineProps> = ({
  toolCalls,
  isLive = false,
  theme,
}) => {
  const { t } = useTranslation();
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<string>>(
    new Set(),
  );

  const toggleToolCallExpanded = (id: string) => {
    setExpandedToolCalls((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (toolCalls.length === 0) return null;

  return (
    <div className="space-y-1">
      {toolCalls.map((tc, idx) => {
        const argsText = formatToolArgs(tc.args);
        const isPending = tc.status === "pending";
        const isCompleted = tc.status === "completed";
        const isLast = idx === toolCalls.length - 1;
        const isExpanded = expandedToolCalls.has(tc.id);
        const hasContent = Boolean(tc.args || tc.result !== undefined);

        return (
          <div
            key={tc.id}
            className={`
              relative pl-4
              ${idx < toolCalls.length - 1 ? "pb-1" : ""}
              animate-[fadeSlideIn_0.15s_ease-out]
            `}
          >
            {/* Timeline line */}
            {idx < toolCalls.length - 1 && (
              <div
                className={`
                  absolute left-[19px] top-5 w-0.5
                  ${isExpanded ? "h-[calc(100%-8px)]" : "h-[calc(100%-8px)]"}
                  ${tc.status === "error" ? "bg-red-500/30" : isCompleted ? "bg-green-500/30" : "bg-[rgba(99,102,241,0.3)]"}
                `}
              />
            )}

            {/* Main row */}
            <div
              className={`
                flex items-start gap-2 cursor-pointer group
                ${hasContent ? "hover:bg-white/5 -mx-2 px-2 py-1 rounded-lg" : ""}
              `}
              onClick={() => hasContent && toggleToolCallExpanded(tc.id)}
            >
              {/* Status indicator */}
              <div
                className={`
                  relative z-10 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full
                  ${
                    isPending && isLive
                      ? "bg-[rgba(99,102,241,0.2)] ring-2 ring-[rgba(99,102,241,0.4)] ring-offset-1 ring-offset-transparent"
                      : isCompleted
                        ? "bg-green-500/20"
                        : isPending
                          ? "bg-[rgba(99,102,241,0.1)]"
                          : "bg-red-500/20"
                  }
                `}
              >
                {isPending && isLive ? (
                  <span className="animate-pulse text-accent">
                    {getToolCallIcon(tc.name, "sm")}
                  </span>
                ) : isCompleted ? (
                  <FaCheck className="h-2.5 w-2.5 text-green-500" />
                ) : isPending ? (
                  <span className="text-accent/60">
                    {getToolCallIcon(tc.name, "sm")}
                  </span>
                ) : (
                  <HiX className="h-2.5 w-2.5 text-red-500" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center gap-2">
                  {hasContent && (
                    <span
                      className={`${theme.textMuted} transition-transform duration-150`}
                    >
                      {isExpanded ? (
                        <FaChevronDown className="h-2 w-2" />
                      ) : (
                        <FaChevronRight className="h-2 w-2" />
                      )}
                    </span>
                  )}
                  <span
                    className={`
                      text-xs font-medium
                      ${isPending && isLive ? "text-accent" : isCompleted ? "text-green-400" : isPending ? "text-accent/60" : "text-red-400"}
                    `}
                  >
                    {getToolCallLabel(tc.name, tc.args)}
                  </span>
                  {isPending && isLive && isLast && (
                    <div className="flex gap-0.5">
                      <div
                        className="h-1 w-1 animate-[bounce_1s_infinite] rounded-full bg-accent"
                        style={{ animationDelay: "0ms", opacity: 0.6 }}
                      />
                      <div
                        className="h-1 w-1 animate-[bounce_1s_infinite] rounded-full bg-accent"
                        style={{ animationDelay: "150ms", opacity: 0.6 }}
                      />
                      <div
                        className="h-1 w-1 animate-[bounce_1s_infinite] rounded-full bg-accent"
                        style={{ animationDelay: "300ms", opacity: 0.6 }}
                      />
                    </div>
                  )}
                  {!isExpanded && isCompleted && tc.result !== undefined && (
                    <span className={`text-[10px] ${theme.textMuted}`}>
                      → {getResultSummary(tc.result, t)}
                    </span>
                  )}
                </div>
                {!isExpanded && argsText && (
                  <p
                    className={`mt-0.5 text-[10px] ${theme.textMuted} font-mono truncate max-w-[280px]`}
                  >
                    {argsText}
                  </p>
                )}
              </div>
            </div>

            {/* Expanded content */}
            {isExpanded && hasContent && (
              <div className="ml-7 mt-2 space-y-2 animate-[fadeSlideIn_0.15s_ease-out]">
                {tc.args && Object.keys(tc.args).length > 0 && (
                  <div>
                    <div
                      className={`text-[10px] font-medium ${theme.textMuted} mb-1 uppercase tracking-wide`}
                    >
                      Arguments
                    </div>
                    <pre
                      className={`
                        text-[10px] ${theme.textMuted} font-mono 
                        bg-black/20 rounded-lg p-2 overflow-x-auto
                        max-h-32 overflow-y-auto
                        whitespace-pre-wrap break-all
                      `}
                    >
                      {formatJsonForDisplay(tc.args)}
                    </pre>
                  </div>
                )}

                {tc.result !== undefined && (
                  <div>
                    <div
                      className={`text-[10px] font-medium ${theme.textMuted} mb-1 uppercase tracking-wide flex items-center gap-2`}
                    >
                      Résultat
                      {Array.isArray(tc.result) && (
                        <span className="text-green-400 normal-case">
                          (
                          {t("n_elements", {
                            count: (tc.result as unknown[]).length,
                          })}
                          )
                        </span>
                      )}
                    </div>
                    <pre
                      className={`
                        text-[10px] ${theme.textMuted} font-mono 
                        bg-black/20 rounded-lg p-2 overflow-x-auto
                        max-h-48 overflow-y-auto
                        whitespace-pre-wrap break-all
                      `}
                    >
                      {formatJsonForDisplay(tc.result, 1000)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

ToolCallsTimeline.displayName = "ToolCallsTimeline";

export default ToolCallsTimeline;
